import { useState } from 'react'
import { Modal, StatusBadge } from './ui'
import { btnPrimary, colors } from './ui/styles'
import { RELEASE_HISTORY, baseMinorVersion, type ReleaseEntry } from '../release-history'

interface Props {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly currentVersion: string
  // When set, only the newest `previewCount` entries are shown initially,
  // with a link to expand the rest. Leave unset to always show the full
  // history (e.g. when opened on demand from the version footer).
  readonly previewCount?: number
}

function formatReleaseDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

// "What's New" release-history modal (issue #236). Renders the curated
// RELEASE_HISTORY newest-first, with a "Current" badge on the entry that
// matches the running version and an optional collapsed preview for the
// once-per-version announce flow. See useReleaseAnnounce for how it's
// triggered automatically.
export function ReleaseHistoryModal({ isOpen, onClose, currentVersion, previewCount }: Props) {
  const [expanded, setExpanded] = useState(false)

  // Collapse back to the preview on close (all close paths -- button, X,
  // overlay -- funnel through Modal's onClose) so a later startup announce
  // opens compact again.
  const handleClose = () => {
    setExpanded(false)
    onClose()
  }

  if (!isOpen) return null

  const currentBase = baseMinorVersion(currentVersion)
  const isPreview = previewCount != null && !expanded && RELEASE_HISTORY.length > previewCount
  const visible = isPreview ? RELEASE_HISTORY.slice(0, previewCount) : RELEASE_HISTORY
  const hiddenCount = RELEASE_HISTORY.length - visible.length

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="What's New" width="560px">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '60vh', overflowY: 'auto' }}>
        {RELEASE_HISTORY.length === 0 ? (
          <p style={{ color: colors.textMuted, margin: 0 }}>No release history available.</p>
        ) : (
          visible.map((entry: ReleaseEntry, idx) => {
            const isCurrent = entry.version === currentBase
            const isLast = idx === visible.length - 1
            return (
              <div
                key={entry.version}
                style={{
                  paddingBottom: '1rem',
                  borderBottom: isLast ? 'none' : `1px solid ${colors.border}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <strong style={{ fontSize: '1rem', color: colors.textHeading }}>Version {entry.version}</strong>
                  {isCurrent && <StatusBadge status="current" label="Current" />}
                  <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: colors.textMuted }}>
                    {formatReleaseDate(entry.date)}
                  </span>
                </div>
                <div style={{ fontWeight: 600, marginTop: '0.25rem', color: colors.text }}>{entry.title}</div>
                <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', color: colors.text }}>
                  {entry.highlights.map((h, hIdx) => (
                    <li key={hIdx} style={{ marginBottom: '0.25rem' }}>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )
          })
        )}
        {isPreview && (
          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => setExpanded(true)}
              style={{
                background: 'none',
                border: 'none',
                color: colors.accent,
                cursor: 'pointer',
                fontSize: '0.875rem',
                padding: 0,
              }}
            >
              Show full history{hiddenCount > 0 ? ` (${hiddenCount} more)` : ''}
            </button>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button type="button" style={btnPrimary} onClick={handleClose}>
          Close
        </button>
      </div>
    </Modal>
  )
}
